/**
 * Keeps the ALTCHA token on the auth screens fresh.
 *
 * The server's challenge expires 5 minutes after it is issued, and the widget
 * solves it on page load. Its own expiry timer is a setTimeout, which stops
 * while the laptop sleeps, so a page left open for hours still shows
 * "Verified" and the next sign-in fails with "ALTCHA verification failed".
 *
 * The auth screens live in core/, so this wraps them from the outside: it
 * notes when the widget was last verified (wall clock, which keeps running
 * through sleep), re-solves when the tab comes back after the token went
 * stale, and holds a stale submit until a fresh token is ready.
 */

/** A little under the server's 5-minute challenge lifetime. */
export const ALTCHA_MAX_AGE_MS = 4 * 60 * 1000;

interface AltchaWidget extends HTMLElement {
  reset: () => void;
  verify: () => Promise<unknown>;
}

export function keepAltchaFresh(root: HTMLElement, now: () => number = () => Date.now()): () => void {
  const widget = root.querySelector<AltchaWidget>('altcha-widget');
  const form = widget?.closest('form');
  if (!widget || !form) return () => {};

  let verifiedAt: number | null = null;
  let expired = false;
  let pendingSubmit = false;

  const stale = () => expired || (verifiedAt !== null && now() - verifiedAt > ALTCHA_MAX_AGE_MS);

  const refresh = () => {
    verifiedAt = null;
    expired = false;
    widget.reset();
    void widget.verify();
  };

  const onState = (e: Event) => {
    const state = (e as CustomEvent<{ state?: string }>).detail?.state;
    // The widget's own timer fired. Re-solve on the next submit or return to
    // the tab, not right away: with a client clock skewed past the server's,
    // every new challenge would expire at once and loop.
    if (state === 'expired') { expired = true; return; }
    if (state !== 'verified') return;
    expired = false;
    verifiedAt = now();
    if (pendingSubmit) {
      pendingSubmit = false;
      form.requestSubmit();
    }
  };

  // Capture on the root runs before core's submit handler on the form.
  const onSubmit = (e: Event) => {
    if (e.target !== form || !stale()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    pendingSubmit = true;
    refresh();
  };

  const onReturn = () => {
    if (document.visibilityState === 'visible' && stale()) refresh();
  };

  widget.addEventListener('statechange', onState);
  root.addEventListener('submit', onSubmit, true);
  document.addEventListener('visibilitychange', onReturn);
  window.addEventListener('focus', onReturn);

  return () => {
    widget.removeEventListener('statechange', onState);
    root.removeEventListener('submit', onSubmit, true);
    document.removeEventListener('visibilitychange', onReturn);
    window.removeEventListener('focus', onReturn);
  };
}
